import { useCallback, useState } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';
import type React from 'react';

import { useDLocal, useDLocalMapError, useDLocalPermanentPaymentMethod } from '#src/app/shared/hooks/payments';
import { useSession } from '#src/app/shared/hooks/session';
import type { DLocalValidationError } from '#src/app/shared/types';
import type { Field, SmartFieldEvent, TokenizationData } from '#src/app/shared/utils/payments';
import { getDlocalToken } from '#src/app/shared/utils/payments';

import { DLocalCreditCardForm } from './DLocalCreditCardForm';

type Props = {
	visible: boolean;
	beforeToken: () => void;
	onTokenError: (err: Error) => void;
	onTokenInvalid: (err: Error) => void;
	onSavePaymentError: (err: Error) => void;
	validate: () => boolean;
	withToken: (paymentType: string, token?: TokenizationData) => void;
	disabled: boolean;
	shouldSavePaymentMethod: boolean;
	nationalId: string;
	loading: boolean;
	onCardNumberFieldFocus: () => void;
	onCardExpiryFieldFocus: () => void;
	onCardCVCFieldFocus: () => void;
	fetchPermanentToken?: boolean;
	useDonationLanguage: boolean;
	cardHolder: string;
	nameInput?: ReactNode;
};

// eslint-disable-next-line max-lines-per-function
export const DLocalCreditCardElements: React.FC<PropsWithChildren<Props>> = ({
	children,
	visible,
	beforeToken,
	onTokenError,
	onTokenInvalid,
	onSavePaymentError,
	validate,
	withToken,
	disabled,
	shouldSavePaymentMethod,
	nationalId,
	loading: parentLoading,
	onCardNumberFieldFocus,
	onCardExpiryFieldFocus,
	onCardCVCFieldFocus,
	fetchPermanentToken,
	useDonationLanguage,
	cardHolder,
	nameInput,
}) => {
	const { sdk } = useDLocal();
	const session = useSession();
	const { mapDLocalValidationError, mapDLocalHttpError, translateDLocalFieldError } = useDLocalMapError();
	const [pan, setPan] = useState<Field | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<DLocalValidationError>({
		card: undefined,
		cvv: undefined,
		expire: undefined,
	});
	const preparePermanentPaymentMethod = useDLocalPermanentPaymentMethod();

	const handleSubmit = useCallback(
		// eslint-disable-next-line max-statements
		async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();
			beforeToken();
			if (!validate()) {
				return;
			}
			setLoading(true);
			const { token, error: dlocalError } = await getDlocalToken(cardHolder, pan, sdk);
			setLoading(false);
			if (dlocalError?.error?.param) {
				const { field, message } = mapDLocalValidationError(dlocalError);
				setError((errors) => ({
					...errors,
					[field]: message,
				}));
				return;
			}
			if (dlocalError) {
				onTokenError(new Error(mapDLocalHttpError(dlocalError)));
				return;
			}
			if (!token) {
				onTokenInvalid(new Error('Failed to get a valid dLocal token'));
				return;
			}

			if (fetchPermanentToken || shouldSavePaymentMethod) {
				try {
					setLoading(true);
					const result = await preparePermanentPaymentMethod({
						token,
						tokenType: 'temporary',
						shouldSavePaymentMethod,
						usage: 'ON_SESSION',
						customer: {
							name: cardHolder,
							countryCode: session.country.countryCode,
							document: nationalId,
							email: session.user?.email as string,
						},
					});
					withToken('creditCard', result);
					setLoading(false);
				} catch (setupError) {
					setLoading(false);
					onSavePaymentError(setupError as Error);
				}
			} else {
				withToken('creditCard', {
					token,
					tokenType: 'temporary',
				});
			}
		},
		[
			beforeToken,
			validate,
			cardHolder,
			pan,
			sdk,
			fetchPermanentToken,
			shouldSavePaymentMethod,
			onTokenError,
			mapDLocalHttpError,
			mapDLocalValidationError,
			onTokenInvalid,
			preparePermanentPaymentMethod,
			session.country.countryCode,
			session.user,
			nationalId,
			onSavePaymentError,
			withToken,
		],
	);

	const updateError = useCallback(
		(field: string, event: SmartFieldEvent) => {
			const errorMessage =
				event.error && (field === 'card' || field === 'cvv' || field === 'expire')
					? translateDLocalFieldError(field)
					: undefined;
			setError((errors) => ({
				...errors,
				[field]: errorMessage,
			}));
		},
		[translateDLocalFieldError],
	);

	return (
		<DLocalCreditCardForm
			visible={visible}
			handleSubmit={handleSubmit}
			error={error}
			disabled={disabled || loading}
			loading={parentLoading || loading}
			useDonationLanguage={useDonationLanguage}
			onCardExpiryFieldFocus={onCardExpiryFieldFocus}
			onCardCVCFieldFocus={onCardCVCFieldFocus}
			onCardNumberFieldFocus={onCardNumberFieldFocus}
			updateError={updateError}
			setPan={setPan}
			nameInput={nameInput}
		>
			{children}
		</DLocalCreditCardForm>
	);
};
